/**
 * Susurro chat engine: wires `@susurro/protocol` (X3DH + double ratchet +
 * sender-keys + safety numbers + expiring messages) over the real transport
 * (blind relay + offline mailbox and prekey directory of `apps/signaling`).
 *
 * Invariants:
 * - **The relay never decrypts**: everything that leaves through `MailboxClient` is an
 *   opaque envelope; the content and private keys live only here.
 * - **Local-first**: the plaintext conversation is persisted on the client (KV) and
 *   never uploaded. Identity and ratchets too.
 *
 * Groups: STAR topology with the creator as distribution hub (glare-free).
 * The hub is a legitimate MEMBER (it can already read the group), so forwarding over
 * its 1:1 sessions does not break E2E; the relay/server still sees nothing.
 */

import {
  acceptDistribution,
  acceptInitiatorSession,
  base64ToBytes,
  bytesToBase64,
  createDeviceIdentity,
  createSenderKey,
  decryptGroupMessage,
  decryptMessage,
  type DeviceIdentity,
  type DirectoryClient,
  distributionMessage,
  encryptGroupMessage,
  encryptMessage,
  formatSafetyNumber,
  type GroupReceiverState,
  isExpired,
  type MailboxClient,
  markInitConfirmed,
  type MessageContent,
  parseX3dhHeader,
  replenishOneTimePreKeys,
  safetyNumber,
  type SenderKeyDistribution,
  type SenderKeyState,
  type Session,
  sign,
  startInitiatorSession,
  utf8Decode,
  utf8Encode,
  verify,
  type WireEnvelope,
} from "@susurro/protocol";

import {
  type AttachChunkControl,
  type AttachControl,
  type Control,
  decodeControl,
  encodeControl,
} from "./control";
import type { CallSignal } from "./call";
import type { P2pSignal } from "./p2p";
import { decryptAttachment, encryptAttachment, type MediaKind } from "./media";
import type {
  ChatMessage,
  Conversation,
  CryptoState,
  MessageAttachment,
  PersistedState,
} from "./types";

/** Input of `sendAttachment`: the plaintext binary + its metadata. */
export interface AttachmentInput {
  readonly name: string;
  readonly mime: string;
  readonly mediaKind: MediaKind;
  readonly bytes: Uint8Array;
  readonly caption?: string;
  readonly durationMs?: number;
  readonly width?: number;
  readonly height?: number;
}

interface PendingAttachment {
  readonly meta: AttachControl;
  readonly chunks: (string | undefined)[];
  received: number;
  /** Device to ack delivery to on completion (1:1 only; in a group there is no ack). */
  readonly receiptPeer?: string;
}

/** Signed identity→devices record published in the directory. */
interface DeviceLinksRecord {
  readonly device: string;
  readonly devices: string[];
  readonly ts: number;
  readonly sig: string;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? { ...value } : {};
}

function parseLinksRecord(blob: string): DeviceLinksRecord | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(blob);
  } catch {
    return undefined;
  }
  const r = asRecord(parsed);
  if (
    typeof r.device !== "string" ||
    !Array.isArray(r.devices) ||
    typeof r.ts !== "number" ||
    typeof r.sig !== "string"
  ) {
    return undefined;
  }
  const devices = r.devices.filter((d): d is string => typeof d === "string");
  return { device: r.device, devices, ts: r.ts, sig: r.sig };
}

/**
 * Canonical payload signed by the owner of the device map: any peer
 * verifies it against the owner's identity key (ECDSA) it already knows.
 */
function linksPayload(device: string, devices: string[], ts: number): Uint8Array {
  return utf8Encode(`susurro:links:v1|${device}|${ts}|${[...devices].toSorted().join(",")}`);
}

/** TTL of the in-memory cache of verified peer device maps. */
const LINKS_CACHE_MS = 30_000;

interface EngineDeps {
  readonly mailbox: MailboxClient;
  readonly directory: DirectoryClient;
  /** Called after each state mutation (persist + refresh UI). */
  readonly onChange: () => void;
  readonly now?: () => number;
  readonly genId?: () => string;
}

function defaultGenId(): string {
  return bytesToBase64(crypto.getRandomValues(new Uint8Array(12))).replaceAll(/[+/=]/g, "");
}

const RECEIVE_OPK_FLOOR = 3;

export class ChatEngine {
  readonly deviceId: string;
  private readonly now: () => number;
  private readonly genId: () => string;
  /**
   * "Link this device" mode: the user opened the linking screen
   * on a NEW device, so we accept an incoming history snapshot.
   * Without this explicit consent we ignore `link-snapshot`
   * (prevents someone from injecting fake conversations you did not ask for).
   */
  private linkMode = false;
  /**
   * Public identity of a peer received via `hello` BEFORE a
   * conversation exists (e.g. the handshake `hello` arrives before the first
   * text, or is part of a linking). Applied when creating the conversation,
   * for the safety number. Ephemeral (re-derivable from future `hello`s).
   */
  private readonly pendingIdentity = new Map<string, { dh: Uint8Array; sig: Uint8Array }>();
  /**
   * DECRYPTED attachment binaries (by `attachId`), memory ONLY: never
   * persisted (avoids bloating localStorage and keeps the plaintext off disk more
   * than necessary). The UI materializes them as a blob URL on demand.
   */
  private readonly attachmentBytes = new Map<string, Uint8Array>();
  /** In-flight incoming attachments (chunks accumulating until complete). */
  private readonly pendingAttachments = new Map<string, PendingAttachment>();
  /** Cache of identity→devices maps VERIFIED per peer (fan-out). */
  private readonly peerLinksCache = new Map<string, { devices: string[]; at: number }>();
  /** Incoming call signal handler (registered by the CallManager). */
  private callHandler?: (peer: string, signal: CallSignal) => void;
  /** Incoming P2P negotiation signal handler (registered by the P2PManager). */
  private p2pHandler?: (peer: string, signal: P2pSignal) => void;
  /**
   * P2P-first direct send: if it returns `true`, the envelope already left through
   * the direct DataChannel and is NOT deposited in the relay (which thus does not even see
   * the ciphertext). If there is no channel, it degrades to the blind mailbox.
   */
  private p2pSender?: (peer: string, envelope: WireEnvelope) => boolean;

  constructor(
    private state: PersistedState,
    private readonly deps: EngineDeps,
  ) {
    this.deviceId = state.identity.deviceId;
    this.now = deps.now ?? (() => Date.now());
    this.genId = deps.genId ?? defaultGenId;
  }

  /** Creates the initial state of a new device (identity + empty collections). */
  static async createInitialState(deviceId: string): Promise<PersistedState> {
    const identity = await createDeviceIdentity(deviceId, 8);
    const crypto: CryptoState = {
      sessions: new Map(),
      senderKeys: new Map(),
      groupReceivers: new Map(),
    };
    return { identity, conversations: [], messages: [], crypto };
  }

  // --- Views (shallow copies for the reactive layer) ------------------------

  get identity(): DeviceIdentity {
    return this.state.identity;
  }

  /** Complete persistable state (owned by the engine; for serializing to KV). */
  get persistedState(): PersistedState {
    return this.state;
  }

  conversations(): Conversation[] {
    return this.state.conversations.toSorted((a, b) => b.lastActivityAt - a.lastActivityAt);
  }

  messagesFor(conversationId: string): ChatMessage[] {
    return this.state.messages
      .filter((m) => m.conversationId === conversationId)
      .filter((m) => !this.isHidden(m))
      .toSorted((a, b) => a.sentAt - b.sentAt);
  }

  private isHidden(m: ChatMessage): boolean {
    return isExpired(
      {
        kind: "text",
        body: m.text,
        sentAt: m.sentAt,
        expiresInSecs: m.expiresInSecs,
      },
      this.now(),
    );
  }

  /** Prunes already expired messages (so they truly "disappear" from the store). */
  pruneExpired(): void {
    const before = this.state.messages.length;
    this.state.messages = this.state.messages.filter((m) => !this.isHidden(m));
    if (this.state.messages.length !== before) {
      this.deps.onChange();
    }
  }

  // --- Startup --------------------------------------------------------------

  /** Claims the own mailbox and publishes the public bundle to the directory. */
  async boot(): Promise<void> {
    await this.deps.mailbox.register();
    await this.deps.directory.publish(this.state.identity);
    // Re-publishes the device map (the directory is ephemeral in memory).
    if (this.ownSiblings().length > 0) {
      await this.publishOwnLinks();
    }
  }

  // --- 1:1 sessions ---------------------------------------------------------

  private findDirectConversation(peerDeviceId: string): Conversation | undefined {
    return this.state.conversations.find(
      (c) => c.kind === "direct" && c.peerDeviceId === peerDeviceId,
    );
  }

  /** Ensures an initiator session with `peer` (fetch bundle + hello). Returns the session. */
  private async ensureInitiatorSession(peerDeviceId: string): Promise<Session> {
    const existing = this.state.crypto.sessions.get(peerDeviceId);
    if (existing) {
      return existing;
    }
    const bundle = await this.deps.directory.fetchBundle(peerDeviceId);
    const session = await startInitiatorSession(this.state.identity, peerDeviceId, bundle);
    this.state.crypto.sessions.set(peerDeviceId, session);
    // Stores the peer identity (from the bundle) for the safety number.
    const conv = this.findDirectConversation(peerDeviceId);
    if (conv) {
      conv.peerIdentityDhPub = bundle.identityDhPub;
      conv.peerIdentitySigPub = bundle.identitySigPub;
    }
    // The first send will be prekey and presents our identity.
    await this.sendControlTo(peerDeviceId, this.helloControl());
    return session;
  }

  private helloControl(): Control {
    return {
      t: "hello",
      idDhPub: bytesToBase64(this.state.identity.identityDh.publicKey),
      idSigPub: bytesToBase64(this.state.identity.identitySig.publicKey),
    };
  }

  /**
   * Opens (or reuses) a 1:1 conversation with a device.
   *
   * RESILIENT: if the peer has not yet published its prekey bundle in the
   * directory (still offline / has not opened the app), we do NOT fail — we create
   * the conversation anyway and **defer** the X3DH session
   * establishment. The polling loop retries it when the peer publishes, and the
   * queued messages are resent on their own (Signal-style asynchronous X3DH). Before,
   * a directory 404 aborted the creation and "Start chat" did nothing.
   */
  async startDirectConversation(peerDeviceId: string, title?: string): Promise<Conversation> {
    let conv = this.findDirectConversation(peerDeviceId);
    if (!conv) {
      conv = {
        id: `d:${peerDeviceId}`,
        kind: "direct",
        title: title ?? peerDeviceId,
        peerDeviceId,
        memberDeviceIds: [this.deviceId, peerDeviceId],
        createdAt: this.now(),
        lastActivityAt: this.now(),
      };
      this.state.conversations.push(conv);
    }
    try {
      await this.ensureInitiatorSession(peerDeviceId);
    } catch (err) {
      // Peer still without published prekeys: the session will be established on retry.
      console.warn("[chat] deferred session (peer not yet available in directory)", err);
    }
    this.deps.onChange();
    return conv;
  }

  // --- Sending --------------------------------------------------------------

  private async encryptAndSend(peerDeviceId: string, content: MessageContent): Promise<void> {
    const session = this.state.crypto.sessions.get(peerDeviceId);
    if (!session) {
      throw new Error(`chat: no session with ${peerDeviceId}`);
    }
    const env = await encryptMessage(this.state.identity, session, content);
    // P2P-first: through the direct DataChannel if open; otherwise, blind mailbox.
    if (this.p2pSender?.(peerDeviceId, env)) {
      return;
    }
    await this.deps.mailbox.send(env);
  }

  private async sendControlTo(peerDeviceId: string, control: Control): Promise<void> {
    await this.encryptAndSend(peerDeviceId, {
      kind: "control",
      body: encodeControl(control),
      sentAt: this.now(),
    });
  }

  /** Sends text to a conversation (1:1 or group). `expiresInSecs` = ephemeral message. */
  async sendText(conversationId: string, text: string, expiresInSecs?: number): Promise<void> {
    const conv = this.state.conversations.find((c) => c.id === conversationId);
    if (!conv) {
      throw new Error(`chat: unknown conversation ${conversationId}`);
    }
    const msgId = this.genId();
    const message: ChatMessage = {
      id: msgId,
      conversationId,
      direction: "out",
      authorDevice: this.deviceId,
      text,
      sentAt: this.now(),
      expiresInSecs,
      status: "sending",
    };
    this.state.messages.push(message);
    conv.lastActivityAt = message.sentAt;
    this.deps.onChange();

    try {
      if (conv.kind === "direct" && conv.peerDeviceId) {
        const content: MessageContent = {
          kind: "text",
          body: text,
          mediaRef: msgId,
          sentAt: message.sentAt,
          expiresInSecs,
        };
        await this.ensureInitiatorSession(conv.peerDeviceId);
        await this.encryptAndSend(conv.peerDeviceId, content);
        // Live fan-out: also to the peer's other VERIFIED devices
        // and outbound echo to my own devices (both best-effort).
        await this.fanoutDirect(conv, content);
        await this.echoToOwnDevices(conv, message);
      } else if (conv.kind === "group" && conv.groupId) {
        await this.sendGroupText(conv, msgId, text, message.sentAt, expiresInSecs);
      }
      message.status = "sent";
    } catch {
      message.status = "failed";
    }
    this.deps.onChange();
  }

  // --- E2E attachments (files / voice notes / photos / video) ---------------

  /** Decrypted bytes of an attachment (to materialize the media in the UI). */
  attachmentBytesFor(attachId: string): Uint8Array | undefined {
    return this.attachmentBytes.get(attachId);
  }

  /**
   * Attaches and sends an E2E-ENCRYPTED binary to a conversation (1:1 or group). The
   * file is encrypted with its own content key and chunked; the key travels
   * inside the `attach` control and the chunks in `attach-chunk`. In 1:1 those
   * controls are ratcheted by the session; in a GROUP they are additionally encrypted with
   * the sender's sender-key inside a `gctl` (the hub forwards in star).
   * The relay only sees opaque ciphertext: never the plaintext nor the key.
   */
  async sendAttachment(
    conversationId: string,
    input: AttachmentInput,
    expiresInSecs?: number,
  ): Promise<void> {
    const conv = this.state.conversations.find((c) => c.id === conversationId);
    if (!conv) {
      throw new Error(`chat: unknown conversation ${conversationId}`);
    }
    if (conv.kind === "direct" && !conv.peerDeviceId) {
      throw new Error("chat: 1:1 conversation without peer");
    }
    if (conv.kind === "group" && !conv.groupId) {
      throw new Error("chat: group conversation without groupId");
    }
    const msgId = this.genId();
    const attachId = this.genId();
    const sentAt = this.now();
    const enc = await encryptAttachment(input.bytes);

    const attachment: MessageAttachment = {
      attachId,
      name: input.name,
      mime: input.mime,
      mediaKind: input.mediaKind,
      size: enc.size,
      sha256: enc.sha256,
      durationMs: input.durationMs,
      width: input.width,
      height: input.height,
      received: enc.chunkCount,
      ready: true,
    };
    // The sender keeps its own plaintext to preview the media.
    this.attachmentBytes.set(attachId, input.bytes);
    const message: ChatMessage = {
      id: msgId,
      conversationId,
      direction: "out",
      authorDevice: this.deviceId,
      text: input.caption ?? "",
      sentAt,
      expiresInSecs,
      attachment,
      status: "sending",
    };
    this.state.messages.push(message);
    conv.lastActivityAt = sentAt;
    this.deps.onChange();

    const announce: AttachControl = {
      t: "attach",
      msgId,
      attachId,
      name: input.name,
      mime: input.mime,
      mediaKind: input.mediaKind,
      size: enc.size,
      sha256: enc.sha256,
      keyB64: enc.keyB64,
      chunkCount: enc.chunkCount,
      sentAt,
      caption: input.caption,
      expiresInSecs,
      durationMs: input.durationMs,
      width: input.width,
      height: input.height,
    };
    const chunkControls: AttachChunkControl[] = enc.chunks.map((data, index) => ({
      t: "attach-chunk",
      attachId,
      index,
      data,
    }));

    try {
      if (conv.kind === "direct" && conv.peerDeviceId) {
        const peer = conv.peerDeviceId;
        await this.ensureInitiatorSession(peer);
        await this.sendAttachmentTo(peer, announce, chunkControls);
        // Live fan-out of the attachment to the peer's other verified devices.
        for (const sibling of await this.fanoutSiblings(conv)) {
          try {
            await this.ensureInitiatorSession(sibling);
            await this.sendAttachmentTo(sibling, announce, chunkControls);
          } catch {
            /* sibling still unpublished: the primary device already received it */
          }
        }
      } else {
        // GROUP: announce + chunks encrypted with the own sender-key (gctl).
        await this.sendGroupControl(conv, announce);
        for (const chunk of chunkControls) {
          await this.sendGroupControl(conv, chunk);
        }
      }
      message.status = "sent";
    } catch {
      message.status = "failed";
    }
    this.deps.onChange();
  }

  private async sendAttachmentTo(
    target: string,
    announce: AttachControl,
    chunks: AttachChunkControl[],
  ): Promise<void> {
    await this.sendControlTo(target, announce);
    for (const chunk of chunks) {
      await this.sendControlTo(target, chunk);
    }
  }

  private handleAttach(
    conv: Conversation,
    author: string,
    control: AttachControl,
    receiptPeer?: string,
  ): void {
    if (this.state.messages.some((m) => m.id === control.msgId)) {
      return; // idempotente (reentregas)
    }
    this.pendingAttachments.set(control.attachId, {
      meta: control,
      chunks: new Array<string | undefined>(control.chunkCount),
      received: 0,
      receiptPeer,
    });
    const attachment: MessageAttachment = {
      attachId: control.attachId,
      name: control.name,
      mime: control.mime,
      mediaKind: control.mediaKind,
      size: control.size,
      sha256: control.sha256,
      durationMs: control.durationMs,
      width: control.width,
      height: control.height,
      received: 0,
      ready: false,
    };
    this.state.messages.push({
      id: control.msgId,
      conversationId: conv.id,
      direction: "in",
      authorDevice: author,
      text: control.caption ?? "",
      sentAt: control.sentAt,
      expiresInSecs: control.expiresInSecs,
      attachment,
      status: "delivered",
    });
    conv.lastActivityAt = Math.max(conv.lastActivityAt, control.sentAt);
  }

  private async handleAttachChunk(control: AttachChunkControl): Promise<void> {
    const pending = this.pendingAttachments.get(control.attachId);
    if (!pending || control.index < 0 || control.index >= pending.meta.chunkCount) {
      return;
    }
    if (pending.chunks[control.index] !== undefined) {
      return; // chunk duplicado
    }
    pending.chunks[control.index] = control.data;
    pending.received += 1;
    const msg = this.state.messages.find((m) => m.attachment?.attachId === control.attachId);
    if (msg?.attachment) {
      msg.attachment.received = pending.received;
    }
    if (pending.received === pending.meta.chunkCount) {
      await this.finalizeAttachment(pending);
    }
  }

  private async finalizeAttachment(pending: PendingAttachment): Promise<void> {
    const { meta } = pending;
    const msg = this.state.messages.find((m) => m.attachment?.attachId === meta.attachId);
    try {
      const bytes = await decryptAttachment({
        keyB64: meta.keyB64,
        chunks: pending.chunks.filter((c): c is string => c !== undefined),
        sha256: meta.sha256,
        size: meta.size,
      });
      this.attachmentBytes.set(meta.attachId, bytes);
      if (msg?.attachment) {
        msg.attachment.received = meta.chunkCount;
        msg.attachment.ready = true;
      }
      // Message ack (like text): the sender marks it "delivered".
      // 1:1 only — in a group there are no acks (same as group text).
      if (pending.receiptPeer) {
        await this.sendControlTo(pending.receiptPeer, { t: "receipt", id: meta.msgId });
      }
    } catch (err) {
      if (msg?.attachment) {
        msg.attachment.failed = true;
      }
      console.warn("[chat] adjunto corrupto o manipulado", err);
    } finally {
      this.pendingAttachments.delete(meta.attachId);
    }
  }

  // --- Calls (WebRTC signaling over the E2E channel) ------------------------

  /** Registers the incoming call signal receiver (CallManager). */
  setCallHandler(handler: (peer: string, signal: CallSignal) => void): void {
    this.callHandler = handler;
  }

  /**
   * Sends a call signal (offer/answer/ICE/hang up) to the peer over the
   * 1:1 ratchet. The relay only sees opaque ciphertext: never the SDP nor the ICE.
   */
  async sendCallSignal(peerDeviceId: string, signal: CallSignal): Promise<void> {
    await this.ensureInitiatorSession(peerDeviceId);
    await this.sendControlTo(peerDeviceId, { t: "call", signal });
  }

  // --- P2P-first transport (direct DataChannel) -----------------------------

  /** Registers the P2P negotiation signal receiver (P2PManager). */
  setP2pHandler(handler: (peer: string, signal: P2pSignal) => void): void {
    this.p2pHandler = handler;
  }

  /** Is there already a ratchet session with that device? (guard to negotiate P2P). */
  hasSessionWith(peerDeviceId: string): boolean {
    return this.state.crypto.sessions.has(peerDeviceId);
  }

  /** Registers the P2P-first direct send (P2PManager.trySend). */
  setP2pSender(sender: (peer: string, envelope: WireEnvelope) => boolean): void {
    this.p2pSender = sender;
  }

  /**
   * Sends a P2P negotiation signal to the peer over the 1:1 ratchet (blind relay:
   * it only sees opaque ciphertext, never the SDP).
   *
   * IMPORTANT: only over an ALREADY existing session — negotiating P2P never OPENS
   * a new X3DH session. If both sides started a session at the same time (e.g. a
   * linked device opening P2P toward a peer that is just fanning out to it),
   * the crossed prekeys would not decrypt (session glare). With no session
   * yet, the P2PManager retries after its throttle.
   */
  async sendP2pSignal(peerDeviceId: string, signal: P2pSignal): Promise<void> {
    if (!this.state.crypto.sessions.has(peerDeviceId)) {
      throw new Error(`chat: no session with ${peerDeviceId} to negotiate P2P`);
    }
    await this.sendControlTo(peerDeviceId, { t: "p2p", signal });
  }

  /**
   * Envelope received over the DIRECT channel (DataChannel): same decryption/
   * application pipeline as the mailbox, without touching the relay.
   */
  async deliverP2pEnvelope(envelope: WireEnvelope): Promise<void> {
    try {
      await this.handleEnvelope(envelope);
    } catch (err) {
      console.warn("[chat] unprocessable P2P envelope", err);
    }
    this.deps.onChange();
  }

  // --- Multi-device (linking: recover history on another device) ------------

  /**
   * Enables receiving a history snapshot (this device is
   * the NEW one being linked). The primary will call `linkDevice(id)`.
   */
  enableLinking(): void {
    this.linkMode = true;
  }

  get linkingEnabled(): boolean {
    return this.linkMode;
  }

  /**
   * Links a new device (the user, on their PRIMARY device, enters the
   * id of the new one). Opens a 1:1 E2E session with it and sends it an encrypted snapshot
   * of all conversations + messages. The relay only sees an opaque blob: the
   * handover travels inside the double ratchet, never in plaintext.
   */
  async linkDevice(targetDeviceId: string): Promise<void> {
    if (targetDeviceId === this.deviceId) {
      throw new Error("chat: cannot link to yourself");
    }
    await this.ensureInitiatorSession(targetDeviceId);
    // Registers the new device as a sibling and publishes the signed map so
    // peers fan out LIVE to all my devices.
    if (!this.ownSiblings().includes(targetDeviceId)) {
      this.state.siblings = [...this.ownSiblings(), targetDeviceId];
    }
    await this.sendControlTo(targetDeviceId, this.buildSnapshot());
    try {
      await this.publishOwnLinks();
    } catch (err) {
      console.warn("[chat] failed to publish device map", err);
    }
    this.deps.onChange();
  }

  // --- Multi-device (live fan-out: signed identity→devices map) --------------

  private ownSiblings(): string[] {
    return this.state.siblings ?? [];
  }

  /**
   * Publishes in the directory the {identity → devices} map SIGNED with the
   * identity key (ECDSA). The directory cannot forge it nor does it gain
   * new information: it only contains routing device ids the relay already sees.
   */
  private async publishOwnLinks(): Promise<void> {
    const devices = [this.deviceId, ...this.ownSiblings()].toSorted();
    const ts = this.now();
    const sig = await sign(
      this.state.identity.identitySig.privateKey,
      linksPayload(this.deviceId, devices, ts),
    );
    const record: DeviceLinksRecord = {
      device: this.deviceId,
      devices,
      ts,
      sig: bytesToBase64(sig),
    };
    await this.deps.directory.publishLinks(this.deviceId, JSON.stringify(record));
  }

  /**
   * Devices from the map published by `peer`, VERIFIED against their known
   * identity key (the same as the safety number). A map with an invalid
   * signature, from another device or that does not include the peer itself is discarded: nobody
   * can sneak in as another's "device of". With cache (short TTL).
   */
  private async verifiedDevicesOf(peer: string, sigPub: Uint8Array): Promise<string[]> {
    const cached = this.peerLinksCache.get(peer);
    if (cached && this.now() - cached.at < LINKS_CACHE_MS) {
      return cached.devices;
    }
    let devices: string[] = [];
    try {
      const blob = await this.deps.directory.fetchLinks(peer);
      if (blob) {
        const rec = parseLinksRecord(blob);
        if (rec && rec.device === peer && rec.devices.includes(peer)) {
          const ok = await verify(
            sigPub,
            linksPayload(rec.device, rec.devices, rec.ts),
            base64ToBytes(rec.sig),
          );
          if (ok) {
            ({ devices } = rec);
          }
        }
      }
    } catch {
      /* directory unavailable: no fan-out in this cycle */
    }
    this.peerLinksCache.set(peer, { devices, at: this.now() });
    return devices;
  }

  /** Verified siblings of the peer of a 1:1 conversation (send fan-out). */
  private async fanoutSiblings(conv: Conversation): Promise<string[]> {
    if (conv.kind !== "direct" || !conv.peerDeviceId || !conv.peerIdentitySigPub) {
      return [];
    }
    const devices = await this.verifiedDevicesOf(conv.peerDeviceId, conv.peerIdentitySigPub);
    return devices.filter((d) => d !== conv.peerDeviceId && d !== this.deviceId);
  }

  /** Sends the same content to the peer's other devices (best-effort). */
  private async fanoutDirect(conv: Conversation, content: MessageContent): Promise<void> {
    for (const sibling of await this.fanoutSiblings(conv)) {
      try {
        await this.ensureInitiatorSession(sibling);
        await this.encryptAndSend(sibling, content);
      } catch {
        /* sibling still without a published bundle: the primary device already received it */
      }
    }
  }

  /** Replicates an OUTGOING message to my other devices (echo, best-effort). */
  private async echoToOwnDevices(conv: Conversation, message: ChatMessage): Promise<void> {
    if (conv.kind !== "direct" || !conv.peerDeviceId) {
      return;
    }
    for (const sibling of this.ownSiblings()) {
      try {
        await this.ensureInitiatorSession(sibling);
        await this.sendControlTo(sibling, {
          t: "echo",
          peer: conv.peerDeviceId,
          msgId: message.id,
          text: message.text,
          sentAt: message.sentAt,
          expiresInSecs: message.expiresInSecs,
        });
      } catch {
        /* best-effort: the sibling will recover it via snapshot on re-linking */
      }
    }
  }

  /**
   * 1:1 conversation where an incoming message from `fromDevice` should land: its
   * direct one if it exists; otherwise, that of the PRIMARY peer whose signed
   * (verified) device map includes `fromDevice` — so replying from a
   * linked device does not fragment the receiver's conversation.
   */
  private async resolveDirectConversation(fromDevice: string): Promise<Conversation> {
    const existing = this.findDirectConversation(fromDevice);
    if (existing) {
      return existing;
    }
    for (const conv of this.state.conversations) {
      if (conv.kind !== "direct" || !conv.peerDeviceId || !conv.peerIdentitySigPub) {
        continue;
      }
      const devices = await this.verifiedDevicesOf(conv.peerDeviceId, conv.peerIdentitySigPub);
      if (devices.includes(fromDevice)) {
        return conv;
      }
    }
    return this.ensureDirectConversationRecord(fromDevice);
  }

  private buildSnapshot(): Control {
    const conversations = this.state.conversations.map((c) => ({
      id: c.id,
      kind: c.kind,
      title: c.title,
      peerDeviceId: c.peerDeviceId,
      groupId: c.groupId,
      memberDeviceIds: [...c.memberDeviceIds],
      createdAt: c.createdAt,
      lastActivityAt: c.lastActivityAt,
      peerIdentityDhPub: c.peerIdentityDhPub ? bytesToBase64(c.peerIdentityDhPub) : undefined,
      peerIdentitySigPub: c.peerIdentitySigPub ? bytesToBase64(c.peerIdentitySigPub) : undefined,
    }));
    const messages = this.state.messages.map((m) => ({
      id: m.id,
      conversationId: m.conversationId,
      direction: m.direction,
      authorDevice: m.authorDevice,
      text: m.text,
      sentAt: m.sentAt,
      expiresInSecs: m.expiresInSecs,
    }));
    return {
      t: "link-snapshot",
      conversations,
      messages,
      siblings: [this.deviceId, ...this.ownSiblings()],
    };
  }

  /** Merges a received snapshot (idempotent by id). Linking mode only. */
  private async ingestSnapshot(control: Extract<Control, { t: "link-snapshot" }>): Promise<void> {
    if (!this.linkMode) {
      return; // without explicit consent, ignore unsolicited snapshots
    }
    for (const c of control.conversations) {
      if (c.id === `d:${this.deviceId}` || this.state.conversations.some((x) => x.id === c.id)) {
        continue;
      }
      this.state.conversations.push({
        id: c.id,
        kind: c.kind,
        title: c.title,
        peerDeviceId: c.peerDeviceId,
        groupId: c.groupId,
        memberDeviceIds: [...c.memberDeviceIds],
        createdAt: c.createdAt,
        lastActivityAt: c.lastActivityAt,
        peerIdentityDhPub: c.peerIdentityDhPub ? base64ToBytes(c.peerIdentityDhPub) : undefined,
        peerIdentitySigPub: c.peerIdentitySigPub ? base64ToBytes(c.peerIdentitySigPub) : undefined,
      });
    }
    for (const m of control.messages) {
      if (this.state.messages.some((x) => x.id === m.id)) {
        continue;
      }
      this.state.messages.push({
        id: m.id,
        conversationId: m.conversationId,
        direction: m.direction,
        authorDevice: m.authorDevice,
        text: m.text,
        sentAt: m.sentAt,
        expiresInSecs: m.expiresInSecs,
        status: m.direction === "out" ? "sent" : "delivered",
      });
    }
    // Adopts the identity's device set and publishes the own
    // signed map: from here on peers also include me in their fan-out.
    if (control.siblings && control.siblings.length > 0) {
      const merged = new Set([...this.ownSiblings(), ...control.siblings]);
      merged.delete(this.deviceId);
      this.state.siblings = [...merged];
      try {
        await this.publishOwnLinks();
      } catch (err) {
        console.warn("[chat] failed to publish device map", err);
      }
    }
  }

  // --- Groups (star topology; the creator is the hub) -----------------------

  /** Creates a group and distributes the invitation + own sender-key to each member. */
  async createGroup(title: string, memberDeviceIds: string[]): Promise<Conversation> {
    const groupId = `g:${this.genId()}`;
    const members = [this.deviceId, ...memberDeviceIds.filter((m) => m !== this.deviceId)];
    const conv: Conversation = {
      id: groupId,
      kind: "group",
      title,
      groupId,
      memberDeviceIds: members,
      createdAt: this.now(),
      lastActivityAt: this.now(),
    };
    this.state.conversations.push(conv);

    const senderKey = await createSenderKey(groupId, this.deviceId);
    this.state.crypto.senderKeys.set(groupId, senderKey);
    this.deps.onChange();

    for (const member of memberDeviceIds) {
      await this.ensureInitiatorSession(member);
      await this.sendControlTo(member, {
        t: "group-invite",
        groupId,
        title,
        members,
        skd: distributionMessage(senderKey),
      });
    }
    return conv;
  }

  private async ownSenderKey(groupId: string): Promise<SenderKeyState> {
    let sk = this.state.crypto.senderKeys.get(groupId);
    if (!sk) {
      sk = await createSenderKey(groupId, this.deviceId);
      this.state.crypto.senderKeys.set(groupId, sk);
    }
    return sk;
  }

  private async sendGroupText(
    conv: Conversation,
    msgId: string,
    text: string,
    sentAt: number,
    expiresInSecs?: number,
  ): Promise<void> {
    const groupId = conv.groupId!;
    const senderKey = await this.ownSenderKey(groupId);
    const gm = await encryptGroupMessage(senderKey, utf8Encode(text));
    const control: Control = {
      t: "gmsg",
      groupId,
      msgId,
      authorDevice: this.deviceId,
      sentAt,
      expiresInSecs,
      gm,
    };
    // Distributes to all the other members with which we have (or open) a session.
    for (const member of conv.memberDeviceIds) {
      if (member === this.deviceId) {
        continue;
      }
      if (this.state.crypto.sessions.has(member)) {
        await this.sendControlTo(member, control);
      }
    }
  }

  /**
   * Sends an internal control (attach / attach-chunk) to a GROUP: the control's
   * JSON is encrypted with the own sender-key (1 encryption per message, signed)
   * and travels as `gctl` over the 1:1 sessions; the hub forwards it in star.
   */
  private async sendGroupControl(conv: Conversation, inner: Control): Promise<void> {
    const groupId = conv.groupId!;
    const senderKey = await this.ownSenderKey(groupId);
    const gm = await encryptGroupMessage(senderKey, utf8Encode(encodeControl(inner)));
    const control: Control = { t: "gctl", groupId, authorDevice: this.deviceId, gm };
    for (const member of conv.memberDeviceIds) {
      if (member === this.deviceId) {
        continue;
      }
      if (this.state.crypto.sessions.has(member)) {
        await this.sendControlTo(member, control);
      }
    }
  }

  // --- Reception ------------------------------------------------------------

  /** One mailbox polling cycle: collects, decrypts and applies each envelope. */
  async poll(): Promise<void> {
    const envelopes = await this.deps.mailbox.poll();
    for (const env of envelopes) {
      try {
        await this.handleEnvelope(env);
      } catch (err) {
        console.warn("[chat] unprocessable envelope", err);
      }
    }
    // Replenish OPKs if the directory emptied us (keeps asynchronous X3DH available).
    if (this.state.identity.oneTimePreKeys.size < RECEIVE_OPK_FLOOR) {
      await replenishOneTimePreKeys(this.state.identity, 8);
      await this.deps.directory.publish(this.state.identity);
    }
    const retried = await this.retryPendingDirect();
    if (envelopes.length > 0 || retried) {
      this.deps.onChange();
    }
  }

  /**
   * Retries the 1:1 conversations with undelivered TEXT messages (status
   * "failed" because the peer had not published its bundle when sent). When
   * the peer appears in the directory, we establish the session and resend the queue
   * — so the first message is delivered as soon as the recipient opens the app
   * (asynchronous X3DH). Returns true if something changed (to refresh the UI).
   */
  private async retryPendingDirect(): Promise<boolean> {
    let changed = false;
    for (const conv of this.state.conversations) {
      if (conv.kind !== "direct" || !conv.peerDeviceId) {
        continue;
      }
      const peer = conv.peerDeviceId;
      const failed = this.state.messages.filter(
        (m) =>
          m.conversationId === conv.id &&
          m.direction === "out" &&
          m.status === "failed" &&
          !m.attachment,
      );
      if (failed.length === 0) {
        continue;
      }
      try {
        await this.ensureInitiatorSession(peer);
      } catch {
        continue; // peer not yet published: retried on next cycle
      }
      for (const m of failed) {
        try {
          await this.encryptAndSend(peer, {
            kind: "text",
            body: m.text,
            mediaRef: m.id,
            sentAt: m.sentAt,
            expiresInSecs: m.expiresInSecs,
          });
          m.status = "sent";
          changed = true;
        } catch {
          /* still "failed": the mailbox did not respond; it is retried */
        }
      }
    }
    return changed;
  }

  private async handleEnvelope(env: WireEnvelope): Promise<void> {
    const peer = env.fromDevice;
    let session = this.state.crypto.sessions.get(peer);
    let justAccepted = false;
    if (session) {
      // We receive something from the peer: if we were initiators, it is now confirmed.
      markInitConfirmed(session);
    } else {
      if (env.type !== "prekey") {
        throw new Error("chat: normal envelope without prior session");
      }
      const header = parseX3dhHeader(env);
      session = await acceptInitiatorSession(
        this.state.identity,
        this.state.identity.signedPreKey.keyPair,
        peer,
        header,
      );
      this.state.crypto.sessions.set(peer, session);
      justAccepted = true;
    }

    // Decrypt FIRST: the responder only gets a sending chain after the
    // first decryption (DH ratchet), so any reply goes afterwards.
    const content = await decryptMessage(session, env);
    if (content.kind === "control") {
      await this.handleControl(peer, content);
    } else if (content.kind === "text") {
      await this.ingestDirectText(peer, content);
      if (content.mediaRef) {
        await this.sendControlTo(peer, { t: "receipt", id: content.mediaRef });
      }
    }

    // As responder, now we can present our identity back.
    if (justAccepted) {
      await this.sendControlTo(peer, this.helloControl());
    }
  }

  private async ingestDirectText(peer: string, content: MessageContent): Promise<void> {
    const conv = await this.resolveDirectConversation(peer);
    const id = content.mediaRef ?? this.genId();
    if (this.state.messages.some((m) => m.id === id)) {
      return; // idempotente (reentregas)
    }
    this.state.messages.push({
      id,
      conversationId: conv.id,
      direction: "in",
      authorDevice: peer,
      text: content.body,
      sentAt: content.sentAt,
      expiresInSecs: content.expiresInSecs,
      status: "delivered",
    });
    conv.lastActivityAt = Math.max(conv.lastActivityAt, content.sentAt);
  }

  private ensureDirectConversationRecord(peer: string): Conversation {
    let conv = this.findDirectConversation(peer);
    if (!conv) {
      conv = {
        id: `d:${peer}`,
        kind: "direct",
        title: peer,
        peerDeviceId: peer,
        memberDeviceIds: [this.deviceId, peer],
        createdAt: this.now(),
        lastActivityAt: this.now(),
      };
      this.state.conversations.push(conv);
      // Applies the peer identity that arrived via a previous `hello` (safety number).
      const pending = this.pendingIdentity.get(peer);
      if (pending) {
        conv.peerIdentityDhPub = pending.dh;
        conv.peerIdentitySigPub = pending.sig;
        this.pendingIdentity.delete(peer);
      }
    }
    return conv;
  }

  private async handleControl(peer: string, content: MessageContent): Promise<void> {
    const control = decodeControl(content.body);
    if (!control) {
      return;
    }
    switch (control.t) {
      case "hello": {
        const dh = base64ToBytes(control.idDhPub);
        const sig = base64ToBytes(control.idSigPub);
        // A `hello` alone does NOT create a visible conversation (avoids empty
        // rows from the handshake / linking): it only attaches the
        // identity if the conversation already exists, or stores it for when it does.
        const conv = this.findDirectConversation(peer);
        if (conv) {
          conv.peerIdentityDhPub = dh;
          conv.peerIdentitySigPub = sig;
        } else {
          this.pendingIdentity.set(peer, { dh, sig });
        }
        break;
      }
      case "receipt": {
        const msg = this.state.messages.find((m) => m.id === control.id && m.direction === "out");
        if (msg && msg.status !== "delivered") {
          msg.status = "delivered";
        }
        break;
      }
      case "group-invite": {
        await this.acceptGroupInvite(peer, control);
        break;
      }
      case "skd": {
        this.acceptSenderKey(control.groupId, control.sender, control.skd);
        await this.relaySenderKeyIfHub(peer, control);
        break;
      }
      case "gmsg": {
        await this.ingestGroupMessage(peer, control);
        break;
      }
      case "gctl": {
        await this.ingestGroupControl(peer, control);
        break;
      }
      case "attach": {
        this.handleAttach(await this.resolveDirectConversation(peer), peer, control, peer);
        break;
      }
      case "attach-chunk": {
        await this.handleAttachChunk(control);
        break;
      }
      case "echo": {
        this.ingestEcho(peer, control);
        break;
      }
      case "call": {
        this.callHandler?.(peer, control.signal);
        break;
      }
      case "p2p": {
        this.p2pHandler?.(peer, control.signal);
        break;
      }
      case "link-snapshot": {
        await this.ingestSnapshot(control);
        break;
      }
    }
  }

  /** Echo from my other devices: registers my own send as OUTGOING. */
  private ingestEcho(from: string, control: Extract<Control, { t: "echo" }>): void {
    if (!this.ownSiblings().includes(from)) {
      return; // only accept echoes from linked sibling devices
    }
    if (this.state.messages.some((m) => m.id === control.msgId)) {
      return; // idempotent
    }
    const conv = this.ensureDirectConversationRecord(control.peer);
    this.state.messages.push({
      id: control.msgId,
      conversationId: conv.id,
      direction: "out",
      authorDevice: from,
      text: control.text,
      sentAt: control.sentAt,
      expiresInSecs: control.expiresInSecs,
      status: "sent",
    });
    conv.lastActivityAt = Math.max(conv.lastActivityAt, control.sentAt);
  }

  private groupReceiverKey(groupId: string, sender: string): string {
    return `${groupId} ${sender}`;
  }

  private acceptSenderKey(groupId: string, sender: string, skd: SenderKeyDistribution): void {
    const receiver: GroupReceiverState = acceptDistribution(skd);
    this.state.crypto.groupReceivers.set(this.groupReceiverKey(groupId, sender), receiver);
  }

  /** The hub forwards a member's sender-key to the rest (so all encrypt/read). */
  private async relaySenderKeyIfHub(
    from: string,
    control: Extract<Control, { t: "skd" }>,
  ): Promise<void> {
    const conv = this.state.conversations.find((c) => c.groupId === control.groupId);
    if (!conv || conv.memberDeviceIds[0] !== this.deviceId) {
      return;
    }
    for (const member of conv.memberDeviceIds) {
      if (member === this.deviceId || member === from || member === control.sender) {
        continue;
      }
      if (this.state.crypto.sessions.has(member)) {
        await this.sendControlTo(member, {
          t: "skd",
          groupId: control.groupId,
          sender: control.sender,
          skd: control.skd,
        });
      }
    }
  }

  private async acceptGroupInvite(
    inviter: string,
    control: Extract<Control, { t: "group-invite" }>,
  ): Promise<void> {
    let conv = this.state.conversations.find((c) => c.groupId === control.groupId);
    if (!conv) {
      conv = {
        id: control.groupId,
        kind: "group",
        title: control.title,
        groupId: control.groupId,
        memberDeviceIds: control.members,
        createdAt: this.now(),
        lastActivityAt: this.now(),
      };
      this.state.conversations.push(conv);
    }
    // Stores the inviter's sender-key.
    this.acceptSenderKey(control.groupId, inviter, control.skd);
    // Creates the own one and distributes it TO THE HUB (the inviter); the hub forwards it.
    const own = await this.ownSenderKey(control.groupId);
    await this.sendControlTo(inviter, {
      t: "skd",
      groupId: control.groupId,
      sender: this.deviceId,
      skd: distributionMessage(own),
    });
  }

  private async ingestGroupMessage(
    from: string,
    control: Extract<Control, { t: "gmsg" }>,
  ): Promise<void> {
    const conv = this.state.conversations.find((c) => c.groupId === control.groupId);
    if (!conv) {
      return;
    }
    const isHub = conv.memberDeviceIds[0] === this.deviceId;

    // If the author is not me, I decrypt and show.
    if (
      control.authorDevice !== this.deviceId &&
      !this.state.messages.some((m) => m.id === control.msgId)
    ) {
      const receiver = this.state.crypto.groupReceivers.get(
        this.groupReceiverKey(control.groupId, control.authorDevice),
      );
      if (receiver) {
        const plain = utf8Decode(await decryptGroupMessage(receiver, control.gm));
        this.state.messages.push({
          id: control.msgId,
          conversationId: conv.id,
          direction: "in",
          authorDevice: control.authorDevice,
          text: plain,
          sentAt: control.sentAt,
          expiresInSecs: control.expiresInSecs,
          status: "delivered",
        });
        conv.lastActivityAt = Math.max(conv.lastActivityAt, control.sentAt);
      }
    }

    // HUB: forwards to the other members (star topology), including the sender-key if it is skd.
    if (isHub) {
      for (const member of conv.memberDeviceIds) {
        if (member === this.deviceId || member === from || member === control.authorDevice) {
          continue;
        }
        if (this.state.crypto.sessions.has(member)) {
          await this.sendControlTo(member, control);
        }
      }
    }
  }

  /**
   * Group internal control (media): decrypts with the author's sender-key and
   * applies the attach / attach-chunk on the group conversation. The hub
   * ALWAYS forwards in star (even if it cannot decrypt yet).
   */
  private async ingestGroupControl(
    from: string,
    control: Extract<Control, { t: "gctl" }>,
  ): Promise<void> {
    const conv = this.state.conversations.find((c) => c.groupId === control.groupId);
    if (!conv) {
      return;
    }
    if (control.authorDevice !== this.deviceId) {
      const receiver = this.state.crypto.groupReceivers.get(
        this.groupReceiverKey(control.groupId, control.authorDevice),
      );
      if (receiver) {
        try {
          const inner = decodeControl(utf8Decode(await decryptGroupMessage(receiver, control.gm)));
          if (inner?.t === "attach") {
            this.handleAttach(conv, control.authorDevice, inner); // grupo: sin acuse
          } else if (inner?.t === "attach-chunk") {
            await this.handleAttachChunk(inner);
          }
        } catch (err) {
          console.warn("[chat] control de grupo no descifrable", err);
        }
      }
    }
    if (conv.memberDeviceIds[0] === this.deviceId) {
      for (const member of conv.memberDeviceIds) {
        if (member === this.deviceId || member === from || member === control.authorDevice) {
          continue;
        }
        if (this.state.crypto.sessions.has(member)) {
          await this.sendControlTo(member, control);
        }
      }
    }
  }

  // --- Safety number --------------------------------------------------------

  async safetyNumberFor(conversationId: string): Promise<string | undefined> {
    const conv = this.state.conversations.find((c) => c.id === conversationId);
    if (!conv || conv.kind !== "direct" || !conv.peerDeviceId) {
      return undefined;
    }
    if (!conv.peerIdentityDhPub || !conv.peerIdentitySigPub) {
      return undefined;
    }
    const raw = await safetyNumber(
      {
        stableId: this.deviceId,
        identityDhPub: this.state.identity.identityDh.publicKey,
        identitySigPub: this.state.identity.identitySig.publicKey,
      },
      {
        stableId: conv.peerDeviceId,
        identityDhPub: conv.peerIdentityDhPub,
        identitySigPub: conv.peerIdentitySigPub,
      },
    );
    return formatSafetyNumber(raw);
  }
}
